import { isCareIndustryPageKey, type CareIndustryPageKey } from './careIndustryLabels.js';
import { careIndustryHeroCopy } from './careIndustryPages.js';
import { isRetailIndustryPageKey, type RetailIndustryPageKey } from './retailIndustryLabels.js';
import { retailIndustryHeroCopy } from './retailIndustryPages.js';
import type { LandingLocale } from './landingContentTypes.js';
import { isIndustryPageKey, type IndustryPageKey } from './industryPages.js';
import type { StaticPageContent } from './staticPageContent.js';

type CompanyHeroCopy = Pick<StaticPageContent, 'title' | 'heroTitleHighlight'> & { intro?: string };
const copy: Record<LandingLocale, Record<Exclude<IndustryPageKey,RetailIndustryPageKey | CareIndustryPageKey>, CompanyHeroCopy>> = {
  "de": {
    "pflege": {
      "title": "Ihr Pflegeteam braucht Zukunft.",
      "heroTitleHighlight": "Zukunft"
    },
    "einzelhandel": {
      "title": "Ihr Einzelhandel braucht Gespür.",
      "heroTitleHighlight": "Gespür"
    },
    "handwerk": {
      "title": "Ihr Handwerk braucht Können.",
      "heroTitleHighlight": "Können"
    },
    "gastro": {
      "title": "Ihr Gastroteam braucht Menschen.",
      "heroTitleHighlight": "Menschen"
    },
    "elektriker": {
      "title": "Ihr Elektroteam braucht Können.",
      "heroTitleHighlight": "Können",
      "intro": "Für Installation, Elektronik und Wartung unterstützen wir Sie bei der Suche nach internationalen Fachkräften – abgestimmt auf Aufgaben und Team."
    },
    "bauwesen": {
      "title": "Ihr Bauteam braucht Verstärkung.",
      "heroTitleHighlight": "Verstärkung",
      "intro": "Ob Tiefbau, Rohrbau oder Trockenbau: Wir helfen Ihnen, internationale Mitarbeitende für Ihre Baustellen zu finden und den Einstieg vorzubereiten."
    },
    "schweisser": {
      "title": "Ihr Betrieb braucht Schweißprofis.",
      "heroTitleHighlight": "Schweißprofis",
      "intro": "Wir unterstützen Sie bei der Suche nach erfahrenen Schweißfachkräften. Verfahren, Werkstoffe und Einsatzbereiche stimmen wir auf Ihren Betrieb ab."
    },
    "cnc": {
      "title": "Ihr CNC-Team braucht Präzision.",
      "heroTitleHighlight": "Präzision",
      "intro": "Für Drehen, Fräsen und CNC-Fertigung finden wir passende internationale Fachkräfte und klären Erfahrung, Maschinenkenntnisse und Teamfit mit Ihnen."
    },
    "shk": {
      "title": "Ihr SHK-Team braucht Verstärkung.",
      "heroTitleHighlight": "Verstärkung",
      "intro": "Für Sanitär, Heizung und Klima bringen wir Ihren Betrieb mit internationalen Fachkräften zusammen und begleiten die Vorbereitung auf den Arbeitsalltag."
    },
    "lkw-fahrer": {
      "title": "Ihr Fuhrpark braucht Fahrer.",
      "heroTitleHighlight": "Fahrer",
      "intro": "Wir unterstützen Sie bei der Suche nach internationalen Fahrern und klären Erfahrung, Fahrerlaubnisse und die Voraussetzungen für Ihren Einsatzbereich."
    }
  },
  "en": {
    "pflege": {
      "title": "Your care team needs people.",
      "heroTitleHighlight": "people"
    },
    "einzelhandel": {
      "title": "Your retail team needs people.",
      "heroTitleHighlight": "people"
    },
    "handwerk": {
      "title": "Your trade team needs skills.",
      "heroTitleHighlight": "skills"
    },
    "gastro": {
      "title": "Your kitchen needs a team.",
      "heroTitleHighlight": "team"
    },
    "elektriker": {
      "title": "Your electrical team needs skills.",
      "heroTitleHighlight": "skills",
      "intro": "We help your business find international professionals for electrical installation, electronics and maintenance, matching experience to your tasks and team."
    },
    "bauwesen": {
      "title": "Your site team needs people.",
      "heroTitleHighlight": "people",
      "intro": "From civil engineering and pipework to drywall, we help you find international staff for your building sites and prepare for their start."
    },
    "schweisser": {
      "title": "Your welding team needs skills.",
      "heroTitleHighlight": "skills",
      "intro": "We help you find experienced international welders, matching their techniques, materials and work experience to the requirements of your business."
    },
    "cnc": {
      "title": "Your CNC team needs precision.",
      "heroTitleHighlight": "precision",
      "intro": "We help your business find international CNC professionals, considering turning and milling experience, machine knowledge and the needs of your team."
    },
    "shk": {
      "title": "Your HVAC team needs skills.",
      "heroTitleHighlight": "skills",
      "intro": "We connect your plumbing, heating and cooling business with international professionals and support their preparation for everyday work."
    },
    "lkw-fahrer": {
      "title": "Your fleet needs drivers.",
      "heroTitleHighlight": "drivers",
      "intro": "We help you find international drivers and review their experience, licences and the requirements of the roles in your fleet together."
    }
  },
  "tr": {
    "pflege": {
      "title": "Bakım ekibiniz güçlensin.",
      "heroTitleHighlight": "güçlensin"
    },
    "einzelhandel": {
      "title": "Mağazanıza güçlü bir ekip.",
      "heroTitleHighlight": "ekip"
    },
    "handwerk": {
      "title": "İşletmenize usta eller.",
      "heroTitleHighlight": "İşletmenize"
    },
    "gastro": {
      "title": "İşletmenize güçlü bir ekip.",
      "heroTitleHighlight": "İşletmenize"
    },
    "elektriker": {
      "title": "Elektrik ekibiniz güçlensin.",
      "heroTitleHighlight": "güçlensin",
      "intro": "Elektrik, montaj ve bakım işleriniz için uluslararası uzmanlar bulmanıza yardımcı oluyoruz. Deneyimlerini, görevlerinize ve ekibinize uygun şekilde değerlendiriyoruz."
    },
    "bauwesen": {
      "title": "Şantiyenize güçlü bir ekip.",
      "heroTitleHighlight": "ekip",
      "intro": "Altyapı, boru döşeme ve alçıpan işleriniz için uluslararası çalışanlar bulmanıza yardımcı oluyor, şantiyenizde işe başlamalarını birlikte hazırlıyoruz."
    },
    "schweisser": {
      "title": "Kaynak ekibiniz güçlensin.",
      "heroTitleHighlight": "güçlensin",
      "intro": "İşletmeniz için deneyimli uluslararası kaynakçılar bulmanıza destek oluyoruz. Kaynak yöntemlerini, malzemeleri ve iş deneyimini ihtiyaçlarınıza göre değerlendiriyoruz."
    },
    "cnc": {
      "title": "CNC ekibinize hassasiyet.",
      "heroTitleHighlight": "hassasiyet",
      "intro": "Tornalama, frezeleme ve CNC üretiminiz için uluslararası ustalar bulmanıza yardımcı oluyoruz. Makine bilgisi, deneyim ve ekibe uyumu birlikte değerlendiriyoruz."
    },
    "shk": {
      "title": "Tesisat ekibiniz güçlensin.",
      "heroTitleHighlight": "güçlensin",
      "intro": "Tesisat, ısıtma ve klima işleriniz için işletmenizi uluslararası uzmanlarla buluşturuyor, günlük iş hayatına hazırlık sürecini birlikte planlıyoruz."
    },
    "lkw-fahrer": {
      "title": "Filonuza güvenilir sürücüler.",
      "heroTitleHighlight": "güvenilir",
      "intro": "Filonuz için uluslararası sürücüler bulmanıza yardımcı oluyoruz. Deneyimlerini, ehliyetlerini ve görevlerinizin gerektirdiği koşulları birlikte değerlendiriyoruz."
    }
  }
};

// Used to create editable company defaults, never to overwrite a saved editorial headline at render time.
export const industryCompanyHeroDefaults = (page: StaticPageContent, locale: LandingLocale): StaticPageContent =>
  isCareIndustryPageKey(page.key) ? {...page,...careIndustryHeroCopy(page.key,locale,'company')} : isRetailIndustryPageKey(page.key) ? {...page,...retailIndustryHeroCopy(page.key,locale,'company')} : isIndustryPageKey(page.key) ? { ...page, ...copy[locale][page.key] } : page;
